/** Configurações › Integrações › Backups: o que copiar, para onde, a que horas, histórico e "Fazer backup agora". */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { confirmar } from "@/ui/feedback";
import { Badge, Card, DataTable, ErrorBox, Field, FormGrid, Loading, Modal, useAct } from "@/ui/kit";

interface Settings { dbSharepoint: boolean; dbS3: boolean; includeStorage: boolean; hour: number; noBackupReason: string | null; updatedAt: string | null; updatedBy: string | null }
interface Run {
  id: string; trigger: "agendado" | "manual" | "terminal"; status: "pedido" | "executando" | "ok" | "erro"; requestedBy: string | null;
  startedAt: string; finishedAt: string | null; dbBytes: number | null; storageBytes: number | null; destinations: string[]; message: string | null;
}
interface BackupInfo {
  settings: Settings;
  available: { sharepoint: boolean; s3: boolean; s3Bucket: string | null; documentsS3: boolean; documentsLabel: string };
  files: { mirrorEnabled: boolean; cacheDays: number };
  lastOkAt: string | null;
  runs: Run[];
}

const mb = (b: number | null) => (b == null ? "—" : b < 1048576 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1048576).toFixed(1)} MB`);
const DEST: Record<string, string> = { sharepoint: "SharePoint", s3: "S3" };
const TRIGGER: Record<string, string> = { agendado: "diário", manual: "pedido na tela", terminal: "servidor" };
const STATUS: Record<string, { label: string; tone: "ok" | "bad" | "info" | "warn" }> = {
  ok: { label: "concluído", tone: "ok" }, erro: { label: "falhou", tone: "bad" }, executando: { label: "executando", tone: "info" }, pedido: { label: "na fila", tone: "warn" },
};

export function BackupSettings(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["config-backups"], queryFn: () => api.get<BackupInfo>("/config/backups"), refetchInterval: 30_000 });
  const [f, setF] = useState<Settings | null>(null);
  const [reasonDlg, setReasonDlg] = useState(false);
  const [reason, setReason] = useState("");
  const inv = [["config-backups"]];
  const save = useAct((r: string | null) => api.put("/config/backups", { ...cur!, reason: r }), inv, () => { setF(null); setReasonDlg(false); setReason(""); });
  const runNow = useAct(() => api.post("/config/backups/executar"), inv, undefined, "Backup pedido: começa em até 5 minutos");
  const mirror = useAct((on: boolean) => api.put("/config/sharepoint", { mirrorEnabled: on }), [...inv, ["config-sharepoint"]]);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const cur = f ?? data.settings;
  const up = (p: Partial<Settings>) => setF({ ...cur, ...p });
  const a = data.available;
  const open = data.runs.find((r) => r.status === "pedido" || r.status === "executando");
  const staleH = data.lastOkAt ? (Date.now() - new Date(data.lastOkAt).getTime()) / 3_600_000 : null;
  const onSave = () => (!cur.dbSharepoint && !cur.dbS3 ? setReasonDlg(true) : save.mutate(null));

  return (
    <div className="stack gap-16">
      {staleH !== null && staleH > 26 && <div className="banner-bad">⚠ O último backup concluído foi em {fmtDateTime(data.lastOkAt)}. Confira o histórico abaixo.</div>}
      {data.settings.noBackupReason && !data.settings.dbSharepoint && !data.settings.dbS3 && <div className="banner-bad">⚠ O banco está sem backup externo. Motivo registrado: {data.settings.noBackupReason}</div>}

      <Card title="Banco de dados" actions={<div className="row nowrap">
        <button className="btn small" disabled={!!open || runNow.isPending} onClick={() => runNow.mutate()}>{open ? (open.status === "pedido" ? "Na fila…" : "Executando…") : "Fazer backup agora"}</button>
        <button className="btn small primary" disabled={!f || save.isPending} onClick={onSave}>Salvar</button>
      </div>}>
        <p className="muted small" style={{ marginTop: 0 }}>
          Cópia diária, cifrada, de todos os dados do sistema (cadastros, atividades, financeiro, movimentações, configurações).
          Só quem tem a chave privada (guardada no AWS Secrets Manager) consegue abrir.
          {data.lastOkAt && <> Último backup concluído: <b>{fmtDateTime(data.lastOkAt)}</b>.</>}
        </p>
        <FormGrid cols={3}>
          <Field label="Destino: SharePoint" hint={a.sharepoint ? 'Pasta "Backups VG APP/AAAA/MM" do site VG APP' : "SharePoint não configurado no servidor"}>
            <label className="row nowrap"><input type="checkbox" checked={cur.dbSharepoint} disabled={!a.sharepoint && !cur.dbSharepoint} onChange={(e) => up({ dbSharepoint: e.target.checked })} /> Enviar ao SharePoint</label>
          </Field>
          <Field label="Destino: Amazon S3" hint={a.s3 ? `Bucket ${a.s3Bucket} (bloqueio contra exclusão)` : "Bucket não configurado no servidor (BACKUP_S3_BUCKET)"}>
            <label className="row nowrap"><input type="checkbox" checked={cur.dbS3} disabled={!a.s3 && !cur.dbS3} onChange={(e) => up({ dbS3: e.target.checked })} /> Enviar ao S3</label>
          </Field>
          <Field label="Horário do backup diário" hint="Horário de Brasília">
            <select className="input" value={cur.hour} onChange={(e) => up({ hour: Number(e.target.value) })}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
            </select>
          </Field>
        </FormGrid>
        {!cur.dbSharepoint && !cur.dbS3 && <div className="banner-bad" style={{ marginTop: 8 }}>Com os dois destinos desligados, o backup fica só no próprio servidor: se ele for perdido, os dados vão junto.</div>}
        <ErrorBox error={save.error ?? runNow.error} />
        {data.settings.updatedAt && <div className="muted small" style={{ marginTop: 8 }}>Última alteração: {fmtDateTime(data.settings.updatedAt)}{data.settings.updatedBy ? ` por ${data.settings.updatedBy}` : ""}.</div>}
      </Card>

      <Card title="Arquivos (documentos)">
        <p className="muted small" style={{ marginTop: 0 }}>
          Os documentos moram no SharePoint (versões, lixeira e a política de retenção do Microsoft Purview protegem contra exclusão).
          A cópia no servidor é só um cache de {data.files.cacheDays} dias.
        </p>
        <FormGrid cols={2}>
          <Field label="Cache do servidor no backup diário" hint="Útil para documentos que ainda não subiram ao SharePoint. Aumenta o tamanho do backup.">
            <label className="row nowrap"><input type="checkbox" checked={cur.includeStorage} onChange={(e) => up({ includeStorage: e.target.checked })} /> Incluir junto do banco</label>
          </Field>
          <Field label={`Cópia dos arquivos do SharePoint no S3 (${data.files.mirrorEnabled ? "ligada" : "desligada"})`}
            hint={a.documentsS3 ? `Destino: ${a.documentsLabel}. Atualizada a cada hora; permite o modo de emergência.` : "Para ligar, o servidor precisa de um bucket e uma chave da AWS para os documentos (S3_BUCKET_DOCUMENTS) — peça o passo a passo."}>
            <button className="btn small" disabled={mirror.isPending || (!a.documentsS3 && !data.files.mirrorEnabled)}
              onClick={() => void confirmar(data.files.mirrorEnabled
                ? "Desligar a cópia dos arquivos no S3? O modo de emergência fica indisponível."
                : "Ligar a cópia dos arquivos do SharePoint no S3? A primeira cópia pode levar algumas horas.").then((ok) => ok && mirror.mutate(!data.files.mirrorEnabled))}>
              {data.files.mirrorEnabled ? "Desligar" : "Ligar"}
            </button>
          </Field>
        </FormGrid>
        <ErrorBox error={mirror.error} />
      </Card>

      <Card title="Histórico">
        <DataTable<Run> rows={data.runs} rowKey={(r) => r.id} empty="Nenhum backup registrado ainda (o histórico começa após esta atualização)." paginate={false}
          columns={[
            { key: "d", label: "Início", width: 140, render: (r) => fmtDateTime(r.startedAt) },
            { key: "t", label: "Origem", width: 150, render: (r) => <>{TRIGGER[r.trigger] ?? r.trigger}{r.requestedBy && r.trigger !== "terminal" && <div className="muted small">{r.requestedBy}</div>}</> },
            { key: "s", label: "Situação", width: 120, render: (r) => <Badge tone={STATUS[r.status]?.tone ?? "info"}>{STATUS[r.status]?.label ?? r.status}</Badge> },
            { key: "w", label: "Para onde", width: 140, render: (r) => r.destinations.map((d) => DEST[d] ?? d).join(" + ") || "—" },
            { key: "z", label: "Tamanho", width: 140, render: (r) => <>{mb(r.dbBytes)}{r.storageBytes != null && <div className="muted small">+ arquivos {mb(r.storageBytes)}</div>}</> },
            { key: "m", label: "Observação", render: (r) => <span className={r.status === "erro" ? "text-bad small" : "muted small"}>{r.message ?? ""}</span> },
          ]} />
        <div className="muted small" style={{ marginTop: 8 }}>Se um backup falhar ou o último concluído passar de 26 horas, os administradores recebem aviso no sino e por e-mail.</div>
      </Card>

      {reasonDlg && (
        <Modal title="Deixar o banco sem backup externo?" onClose={() => setReasonDlg(false)} footer={
          <><button className="btn" onClick={() => setReasonDlg(false)}>Cancelar</button>
            <button className="btn danger" disabled={reason.trim().length < 10 || save.isPending} onClick={() => save.mutate(reason.trim())}>Confirmar</button></>
        }>
          <p>O backup do banco vai ficar só no servidor. Se o servidor for perdido, os dados não terão cópia. Fica registrado na auditoria.</p>
          <Field label="Motivo (mínimo 10 caracteres)" required><textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
          <ErrorBox error={save.error} />
        </Modal>
      )}
    </div>
  );
}
